/**
 * Which of the site's two canonical section pairs each page's closing band and
 * footer are painted with.
 *
 * The rule is contrast, not preference. A band painted the same colour as the
 * surface above it has no edge and stops reading as a band; the same is true of a
 * footer sitting directly under a band of its own colour. So each surface takes
 * the opposite tone to the one before it:
 *
 *     section above  ->  band  ->  footer
 *     grey               white     grey
 *     white              grey      white
 *
 * Both values come from ONE list, so the band and the footer can never drift out
 * of step. That is the failure this table exists to prevent. DMX-025 happened
 * because the same styling decision was duplicated in 24 places.
 *
 * The tones themselves are the two pairs already used across the codebase:
 *   grey  = bg-gray-50 dark:bg-gray-800   (~110 other uses)
 *   white = bg-white   dark:bg-gray-900   (~30 other uses)
 */
export type SectionTone = 'grey' | 'white'

/**
 * Routes whose closing band sits on a WHITE surface, and therefore needs the grey
 * tone to have an edge. Every other route's band sits on grey and stays white.
 *
 * Derived from measuring the rendered pages, not from reading the source: several
 * pages get their final section from a child component, so grepping the page file
 * gives the wrong answer.
 */
export const GREY_BAND_ROUTES = [
  '/',
  '/about',
  '/abu-dhabi-services',
  '/devops-services-dubai',
  '/software-development-dubai',
  '/aws-cloud-consulting-dubai',
  '/kubernetes-consulting-dubai',
  '/managed-it-services-dubai',
  '/ecommerce-development-dubai',
  '/it-companies-dubai',
  '/it-consulting-companies-dubai',
  '/blog/cloud-migration-cost-uae',
  '/blog/web-development-agency-dubai',
  '/blog/enterprise-software-dubai',
] as const

/**
 * Trailing slashes are stripped at the CDN and React Router matches paths
 * case-insensitively, so normalise both before looking a route up.
 */
export const normaliseRoute = (pathname: string) =>
  (pathname.replace(/\/+$/, '') || '/').toLowerCase()

export const bandTone = (pathname: string): SectionTone =>
  (GREY_BAND_ROUTES as readonly string[]).includes(normaliseRoute(pathname)) ? 'grey' : 'white'

/** Always the opposite of the band, so the footer has an edge against it. */
export const footerTone = (pathname: string): SectionTone =>
  bandTone(pathname) === 'grey' ? 'white' : 'grey'

export const TONE_CLASSES: Record<SectionTone, string> = {
  grey: 'bg-gray-50 dark:bg-gray-800',
  white: 'bg-white dark:bg-gray-900',
}

/**
 * The class components/CanvasTone puts on <html> so the CANVAS, the surface iOS
 * Safari samples to tint the strip behind its bottom toolbar, takes the footer's
 * tone rather than a fixed colour that is wrong on half the routes.
 *
 * It has to be a class rather than a Tailwind utility: the rules live in
 * src/index.css keyed off `html.canvas-grey`, because Tailwind's `dark:` variant
 * compiles to `:is(.dark *)`, a descendant match that can never target html.
 *
 * Only the grey tone is named. White is the resting state, so the class is simply
 * absent, the same shape as the `dark` class, and one less thing to keep in step.
 */
export const CANVAS_GREY_CLASS = 'canvas-grey'

/** True when this route's canvas should take the grey tone. */
export const canvasIsGrey = (pathname: string) => footerTone(pathname) === 'grey'
